import {Box, CircularProgress} from '@mui/material'
import {uniqBy} from 'lodash'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {Banner} from '../../../../../features/rmc/orderIntake/components/shared/Banner'
import {TimeSlotProps} from '../types'
import {groupSlotsByTimeOfTheDay} from '../utils/index'

import {TimeSlotAlert} from './TimeSlotAlert'
import {TimeSlotSelection} from './TimeSlotSelection'

export const TimeSlotGrid = ({
  setSelectedDeliveryTimeSlot,
  selectedDeliveryTimeSlot,
  onChange,
  slots,
  isLoading,
  selectedDate,
  isSuccess,
  setSelectedSlotSiloConfig,
  setSelectedSlot,
  isError,
  externalReference
}: TimeSlotProps) => {
  const {t: translate} = useTranslation()
  const formatDate = selectedDate ? selectedDate.format('D MMMM') : ''

  const freeSlots = useMemo(() => {
    if (!isSuccess || slots.length === 0) return []
    // get unique slots by start time first, then filter for free slots only
    let availableSlots = uniqBy(slots, (slot) => slot.slotConfig?.slotStart).filter(
      (slot) => slot.slotStatus?.statusName === 'FR'
    )

    const selected = slots.find(
      (slot) => slot.slotConfig?.slotStart === selectedDeliveryTimeSlot?.earliest
    )
    // check if selected slot is not in available slots, then add it to the list for edit case
    if (
      selected &&
      !availableSlots.some(
        (item) => item.slotConfig?.slotStart === selectedDeliveryTimeSlot?.earliest
      )
    ) {
      availableSlots = [selected, ...availableSlots]
    }
    return availableSlots.sort((a, b) =>
      a.slotConfig.slotStart.localeCompare(b.slotConfig.slotStart)
    )
  }, [isSuccess, slots])

  React.useEffect(() => {
    const selected = slots.find(
      (slot) => slot.slotConfig?.slotStart === selectedDeliveryTimeSlot?.earliest
    )
    if (selected) {
      setSelectedSlot(selected)
    }
  }, [slots, selectedDeliveryTimeSlot?.earliest, setSelectedSlot])

  // Categorize slots by time of day
  const {morningSlots, afternoonSlots, eveningSlots} = groupSlotsByTimeOfTheDay(freeSlots)

  const timeSlotSections = useMemo(() => {
    return [
      {title: translate('orderIntake.rmc.steps.time.morning'), slots: morningSlots},
      {title: translate('orderIntake.rmc.steps.time.afternoon'), slots: afternoonSlots},
      {title: translate('orderIntake.rmc.steps.time.evening'), slots: eveningSlots}
    ].filter((section) => section.slots.length > 0)
  }, [morningSlots, afternoonSlots, eveningSlots, translate])

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}
    >
      {isLoading ? (
        <div
          style={{
            padding: '2rem',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem'
          }}
        >
          <CircularProgress size={24} />
          <p>{translate('orderIntake.rmc.steps.shared.loading')}</p>
        </div>
      ) : (
        <>
          {/* Warning Banner if slot is already booked */}
          {isError && (
            <Banner
              variant="warning"
              message={translate(
                'orderIntake.rmc.steps.shared.validationErrors.reservationWindowBookedAlertTitle'
              )}
            />
          )}

          {/* Warning Banner if no slot is found for a date */}
          {isSuccess && !freeSlots.length && (
            <Banner
              variant="warning"
              message={translate('orderIntake.rmc.steps.shared.noSlotData', {formatDate})}
            />
          )}

          {isSuccess && freeSlots.length > 0 && (
            <>
              {/* Alert Message */}
              {selectedDeliveryTimeSlot?.earliest && !isError && <TimeSlotAlert />}

              {/* Main Content */}
              <Box
                sx={{flex: 1, display: 'flex', flexDirection: 'column', gap: '16px'}}
                style={{minHeight: 100, maxHeight: '30vh', overflowY: 'auto', padding: 10}}
              >
                {/* Slot time selection */}
                {timeSlotSections.map((item) => (
                  <TimeSlotSelection
                    key={item.title}
                    slots={item.slots}
                    title={item.title}
                    selectedDeliveryTimeSlot={selectedDeliveryTimeSlot}
                    setSelectedDeliveryTimeSlot={setSelectedDeliveryTimeSlot}
                    setSelectedSlotSiloConfig={setSelectedSlotSiloConfig}
                    setSelectedSlot={setSelectedSlot}
                    onChange={onChange}
                    externalReference={externalReference}
                  />
                ))}
              </Box>

              {/* Error Banner if no slot is selected */}
              {!selectedDeliveryTimeSlot?.earliest && (
                <Banner
                  variant="error"
                  message={translate('orderIntake.rmc.steps.shared.validationErrors.slotRequired')}
                  testId="slot-selection-banner"
                />
              )}
            </>
          )}
        </>
      )}
    </Box>
  )
}
